import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";
import { Button } from "@natera/form";
import { usePhoneValidation } from "@natera/platform/lib/hooks";
import { SpinnerView } from "@app/components";
import { ConfigContext } from "@app/provider";
import {
  formatPastedPhoneNumber,
  formatPhoneNumberInput,
  stripPhoneFormatting,
  validateEmail,
} from "@app/utils";
import ArrowBack from "@app/postBill/assets/svg/arrowNavBack.svg";
import { routes } from "@app/routing";
import { GisIntakeStep, STEP_ROUTE_SUFFIX } from "@app/service/intakeForm";
import { useGisIntakeFormData } from "@app/hooks/useGisIntakeFormData";
import {
  useIntakeForm,
  useGisGuest,
  readGuestLookupPrefill,
  writeGuestLookupPrefill,
} from "@app/pages/private/scheduleAGeneticInformationSession/context";
import { useGisSessionOps } from "@app/pages/private/scheduleAGeneticInformationSession/useGisSessionOps";
import { buildLookupPrefilledInput, resolveLookupReturnUrl } from "../helpers";

import "@app/pages/private/scheduleAGeneticInformationSession/intakeFormConfirmInfo.scss";

const messages = defineMessages({
  pageTitle: {
    id: "guestNoOrderConfirmInfo.pageTitle",
    defaultMessage: "Genetic Information Session",
  },
  stepIndicator: {
    id: "guestNoOrderConfirmInfo.stepIndicator",
    defaultMessage: "Step {current} of {total}",
  },
  mainTitle: {
    id: "guestNoOrderConfirmInfo.mainTitle",
    defaultMessage:
      "We couldn’t find any test results under your name. Please fill out this form to schedule a session.",
  },
  mobileNumber: {
    id: "guestNoOrderConfirmInfo.mobileNumber",
    defaultMessage: "Mobile number",
  },
  email: {
    id: "guestNoOrderConfirmInfo.email",
    defaultMessage: "Email",
  },
  nextButton: {
    id: "guestNoOrderConfirmInfo.nextButton",
    defaultMessage: "Next",
  },
  backButton: {
    id: "guestNoOrderConfirmInfo.backButton",
    defaultMessage: "Back",
  },
  fieldRequired: {
    id: "guestNoOrderConfirmInfo.fieldRequired",
    defaultMessage: "Please fill in this field.",
  },
  phoneInvalid: {
    id: "guestNoOrderConfirmInfo.phoneInvalid",
    defaultMessage: "Please enter a valid phone number.",
  },
  emailInvalid: {
    id: "guestNoOrderConfirmInfo.emailInvalid",
    defaultMessage: "Please enter a valid email address.",
  },
});

/**
 * No-order variant of the guest GIS intake "confirm info" step (the Figma
 * "fill out this form to schedule a session" screen). Reached when the public
 * order-lookup found no GIS-eligible order. Identity details are retained from
 * the lookup step; this screen only lets the visitor add or edit contact info.
 */
const GuestNoOrderConfirmInfo: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { config } = useContext(ConfigContext);
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();

  const { token: guestToken } = useGisGuest();
  const {
    confirmedPhone: contextPhone,
    confirmedEmail: contextEmail,
    prefilledData,
    setConfirmedContact,
    setPrefilledData,
    totalSteps,
    getStepNumber,
    getNextStep,
  } = useIntakeForm();
  const { getActiveSession, updateStep, markIntakeStarted } =
    useGisSessionOps();

  const { intakeFormData, isLoading: intakeFormLoading } = useGisIntakeFormData(
    orderUidPublic ?? null,
    testUid ?? null,
    guestToken ?? null
  );

  const { validatePhone: validatePhoneFormat } = usePhoneValidation();

  const [phoneValue, setPhoneValue] = useState("");
  const [emailValue, setEmailValue] = useState("");

  const [errors, setErrors] = useState<{
    phone?: string;
    email?: string;
  }>({});

  // Details the visitor typed in the order-lookup funnel, stashed just before
  // navigating here. This is the only prefill for the no-order form: there's no
  // matched order to prefill from, and the server deliberately returns no
  // demographics for a no-order guest token (it can't confirm who the visitor
  // is without also confirming they're a patient). Read once on mount.
  const lookupPrefill = useMemo(() => readGuestLookupPrefill(), []);

  // Seed the intake context prefill once so downstream steps and the scheduler
  // read a consistent snapshot. Guarded on the current prefill so it never
  // clobbers values the visitor later confirms via Next (which persist across
  // step remounts).
  useEffect(() => {
    if (prefilledData) return;
    if (intakeFormLoading) return;
    if (!intakeFormData && !lookupPrefill) return;

    setPrefilledData({
      firstName: lookupPrefill?.firstName || intakeFormData?.firstName || null,
      lastName: lookupPrefill?.lastName || intakeFormData?.lastName || null,
      dateOfBirth:
        lookupPrefill?.dateOfBirth || intakeFormData?.dateOfBirth || null,
      phone: lookupPrefill?.phone || intakeFormData?.phone || null,
      email: lookupPrefill?.email || intakeFormData?.email || null,
      zipCode: lookupPrefill?.zipCode || intakeFormData?.zipCode || null,
      physicianClinic: intakeFormData?.physicianClinic ?? null,
      orderingPhysician: intakeFormData?.orderingPhysician ?? null,
      cancerType: intakeFormData?.cancerType ?? null,
      cancerStage: intakeFormData?.cancerStage ?? null,
      testType: intakeFormData?.testType ?? null,
      hasGISAppointment: intakeFormData?.hasGISAppointment ?? null,
      hasPreviouslyTakenTest: intakeFormData?.hasPreviouslyTakenTest ?? null,
    });
  }, [
    prefilledData,
    intakeFormLoading,
    intakeFormData,
    lookupPrefill,
    setPrefilledData,
  ]);

  // Seed editable contact fields from what the visitor typed in the lookup;
  // the server sends no contact details for a no-order guest.
  useEffect(() => {
    const phoneSource =
      lookupPrefill?.phone || contextPhone || intakeFormData?.phone || "";
    const emailSource =
      lookupPrefill?.email || contextEmail || intakeFormData?.email || "";

    setPhoneValue(phoneSource ? formatPhoneNumberInput(phoneSource) : "");
    setEmailValue(emailSource);
  }, [
    lookupPrefill,
    intakeFormData?.phone,
    intakeFormData?.email,
    contextPhone,
    contextEmail,
  ]);

  const currentStep = getStepNumber(GisIntakeStep.CONFIRM_INFO);
  const progressPercentage = (currentStep / totalSteps) * 100;

  const clearError = (field: keyof typeof errors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handlePhoneChange = (value: string) => {
    setPhoneValue(formatPhoneNumberInput(value));
    clearError("phone");
  };

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    setPhoneValue(formatPastedPhoneNumber(e.clipboardData.getData("text")));
    clearError("phone");
  };

  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const strippedPhone = stripPhoneFormatting(phoneValue);
  const trimmedEmail = emailValue.trim();
  const phoneIsValid =
    !!strippedPhone &&
    strippedPhone !== "+" &&
    validatePhoneFormat(strippedPhone);
  const emailIsValid =
    !!trimmedEmail && validateEmail(trimmedEmail, usePlusSignInEmailAddress);
  const canContinue = phoneIsValid && emailIsValid;

  const validate = (): {
    strippedPhone: string;
    ok: boolean;
  } => {
    const nextErrors: typeof errors = {};

    if (!strippedPhone || strippedPhone === "+") {
      nextErrors.phone = intl.formatMessage(messages.fieldRequired);
    } else if (!validatePhoneFormat(strippedPhone)) {
      nextErrors.phone = intl.formatMessage(messages.phoneInvalid);
    }

    if (!trimmedEmail) {
      nextErrors.email = intl.formatMessage(messages.fieldRequired);
    } else if (!validateEmail(trimmedEmail, usePlusSignInEmailAddress)) {
      nextErrors.email = intl.formatMessage(messages.emailInvalid);
    }

    setErrors(nextErrors);
    return {
      strippedPhone,
      ok: Object.keys(nextErrors).length === 0,
    };
  };

  const handleBack = () => {
    writeGuestLookupPrefill({
      firstName: lookupPrefill?.firstName ?? prefilledData?.firstName ?? "",
      lastName: lookupPrefill?.lastName ?? prefilledData?.lastName ?? "",
      dateOfBirth:
        lookupPrefill?.dateOfBirth ?? prefilledData?.dateOfBirth ?? "",
      phone: strippedPhone,
      email: trimmedEmail,
      zipCode: lookupPrefill?.zipCode ?? prefilledData?.zipCode ?? "",
      lookupIdentifier: lookupPrefill?.lookupIdentifier,
    });
    // The lookup form: a visitor with no eligible order never saw the list of
    // tests. Naming it keeps Back on the funnel even for someone who walked
    // forward into the intake and stepped back out to this screen.
    navigate(resolveLookupReturnUrl());
  };

  const handleNext = () => {
    const { strippedPhone, ok } = validate();
    if (!ok) return;

    setConfirmedContact(strippedPhone, trimmedEmail);

    // Preserve immutable identity details from lookup while syncing edits.
    writeGuestLookupPrefill({
      firstName: lookupPrefill?.firstName ?? prefilledData?.firstName ?? "",
      lastName: lookupPrefill?.lastName ?? prefilledData?.lastName ?? "",
      dateOfBirth:
        lookupPrefill?.dateOfBirth ?? prefilledData?.dateOfBirth ?? "",
      phone: strippedPhone,
      email: trimmedEmail,
      zipCode: lookupPrefill?.zipCode,
      lookupIdentifier: lookupPrefill?.lookupIdentifier,
    });

    // Fire-and-forget: persist the confirmed contact to the no-order guest
    // triage row (created up-front by the order-lookup funnel) and stamp
    // intake_started. Never block scheduling on this.
    if (orderUidPublic && testUid) {
      void (async () => {
        try {
          const session = await getActiveSession({ orderUidPublic, testUid });
          const sessionId = session?.id;
          if (sessionId == null) return;
          await updateStep({
            sessionId,
            input: {},
            confirmedPhone: strippedPhone,
            confirmedEmail: trimmedEmail,
            prefilledInput: {
              ...buildLookupPrefilledInput(lookupPrefill, validatePhoneFormat),
              phone: strippedPhone,
              email: trimmedEmail,
            },
          });
          await markIntakeStarted({ sessionId });
        } catch (err) {
          console.error("Failed to persist no-order guest contact:", err);
        }
      })();
    }

    const nextStep =
      getNextStep(GisIntakeStep.CONFIRM_INFO) ?? GisIntakeStep.SELECT_TEST;
    navigate(
      routes.scheduleAGeneticInformationSessionIntakePage(
        orderUidPublic || "",
        testUid || ""
      ) + STEP_ROUTE_SUFFIX[nextStep]
    );
  };

  return (
    <div className="intake-form">
      <SpinnerView isLoading={intakeFormLoading} />
      <div className="intake-form__header">
        <button
          className="intake-form__header__back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          <Svg content={ArrowBack} />
        </button>
        <h1 className="intake-form__header__title">
          {intl.formatMessage(messages.pageTitle)}
        </h1>
        <div className="intake-form__header__placeholder" />
      </div>

      <div className="intake-form__content">
        <div className="intake-form__card-container">
          <div className="intake-form__progress-bar">
            <div
              className="intake-form__progress-bar__fill"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          <div className="intake-form__card">
            <div className="intake-form__title-section">
              <span className="intake-form__step-indicator">
                {intl.formatMessage(messages.stepIndicator, {
                  current: currentStep,
                  total: totalSteps,
                })}
              </span>
              <h2 className="intake-form__main-title">
                {intl.formatMessage(messages.mainTitle)}
              </h2>
            </div>

            <div className="intake-form__fields">
              <div className="intake-form__field">
                <label
                  className="intake-form__field__label"
                  htmlFor="no-order-mobile"
                >
                  {intl.formatMessage(messages.mobileNumber)}
                  <span className="intake-form__field__required" aria-hidden>
                    *
                  </span>
                </label>
                <input
                  id="no-order-mobile"
                  type="tel"
                  className={`intake-form__field__input ${
                    errors.phone ? "intake-form__field__input--error" : ""
                  }`}
                  value={phoneValue}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onPaste={handlePhonePaste}
                  placeholder="+1-123-456-7897"
                  aria-invalid={!!errors.phone}
                />
                {errors.phone && (
                  <span className="intake-form__field__error" role="alert">
                    {errors.phone}
                  </span>
                )}
              </div>

              <div className="intake-form__field">
                <label
                  className="intake-form__field__label"
                  htmlFor="no-order-email"
                >
                  {intl.formatMessage(messages.email)}
                  <span className="intake-form__field__required" aria-hidden>
                    *
                  </span>
                </label>
                <input
                  id="no-order-email"
                  type="email"
                  className={`intake-form__field__input ${
                    errors.email ? "intake-form__field__input--error" : ""
                  }`}
                  value={emailValue}
                  onChange={(e) => {
                    setEmailValue(e.target.value);
                    clearError("email");
                  }}
                  placeholder="jsmith@gmail.com"
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <span className="intake-form__field__error" role="alert">
                    {errors.email}
                  </span>
                )}
              </div>
            </div>

            <div className="intake-form__actions">
              <Button
                className="intake-form__actions__confirm"
                unelevated
                disabled={!canContinue}
                onClick={handleNext}
              >
                {intl.formatMessage(messages.nextButton)}
              </Button>
              <button
                className="intake-form__actions__back"
                onClick={handleBack}
              >
                {intl.formatMessage(messages.backButton)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestNoOrderConfirmInfo;
